import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';

type ToastSeverity = 'success' | 'info' | 'warn' | 'error';

interface ErrorCopy {
  summary: string;
  detail: string;
}

@Injectable({ providedIn: 'root' })
export class ApiToastService {
  private readonly messages = inject(MessageService);
  private readonly recentMessages = new Map<string, number>();
  private readonly duplicateWindowMs = 1500;

  success(summary: string, detail = 'La operación finalizó correctamente.'): void {
    this.show('success', summary, detail, 3800);
  }

  info(summary: string, detail: string): void {
    this.show('info', summary, detail, 4500);
  }

  warning(summary: string, detail: string): void {
    this.show('warn', summary, detail, 5500);
  }

  fromHttpError(error: HttpErrorResponse, contextualDetail?: string): void {
    const copy = this.describeHttpError(error, contextualDetail);
    this.show('error', copy.summary, copy.detail, 7000);
  }

  private show(
    severity: ToastSeverity,
    summary: string,
    detail: string,
    life: number,
  ): void {
    const now = Date.now();
    const deduplicationKey = `${severity}:${summary}:${detail}`;
    const lastShownAt = this.recentMessages.get(deduplicationKey) ?? 0;

    if (now - lastShownAt < this.duplicateWindowMs) return;

    this.recentMessages.set(deduplicationKey, now);
    if (this.recentMessages.size > 50) {
      for (const [key, timestamp] of this.recentMessages) {
        if (now - timestamp > this.duplicateWindowMs) this.recentMessages.delete(key);
      }
    }

    this.messages.add({
      severity,
      summary,
      detail,
      life,
      closable: true,
    });
  }

  private describeHttpError(error: HttpErrorResponse, contextualDetail?: string): ErrorCopy {
    if (error.status === 0) {
      return {
        summary: 'Sin conexión con el servidor',
        detail:
          contextualDetail ??
          'No pudimos comunicarnos con la API. Comprueba tu conexión e inténtalo nuevamente.',
      };
    }

    const statusCopy: Record<number, ErrorCopy> = {
      400: {
        summary: 'Solicitud no válida',
        detail: 'Revisa la información enviada e inténtalo nuevamente.',
      },
      401: {
        summary: 'La sesión ha caducado',
        detail: 'Vuelve a iniciar sesión para continuar.',
      },
      403: {
        summary: 'Acceso denegado',
        detail: 'Tu cuenta no tiene permisos para realizar esta acción.',
      },
      404: {
        summary: 'Información no encontrada',
        detail: 'El recurso solicitado no existe o ya no está disponible.',
      },
      409: {
        summary: 'No se pudo completar la operación',
        detail: 'La información cambió o existe un conflicto con el estado actual.',
      },
      410: {
        summary: 'El recurso ya no está disponible',
        detail: 'La información solicitada ha caducado o fue retirada.',
      },
      422: {
        summary: 'Hay datos que necesitan revisión',
        detail: 'Corrige los campos indicados e inténtalo nuevamente.',
      },
      429: {
        summary: 'Demasiadas solicitudes',
        detail: 'Espera unos segundos antes de volver a intentarlo.',
      },
      500: {
        summary: 'Error interno del servidor',
        detail: 'El servidor no pudo completar la operación. Inténtalo nuevamente más tarde.',
      },
      502: {
        summary: 'Servicio temporalmente no disponible',
        detail: 'Uno de los servicios necesarios no está respondiendo.',
      },
      503: {
        summary: 'Servicio temporalmente no disponible',
        detail: 'El sistema está ocupado o en mantenimiento. Inténtalo nuevamente más tarde.',
      },
      504: {
        summary: 'El servidor tardó demasiado',
        detail: 'No recibimos respuesta a tiempo. Inténtalo nuevamente.',
      },
    };
    const fallback = statusCopy[error.status] ?? {
      summary: 'No se pudo completar la operación',
      detail: 'Ocurrió un problema inesperado. Inténtalo nuevamente.',
    };
    const backendDetail = error.status < 500 ? this.extractBackendDetail(error.error) : null;

    return {
      summary: fallback.summary,
      detail: contextualDetail ?? backendDetail ?? fallback.detail,
    };
  }

  private extractBackendDetail(payload: unknown): string | null {
    if (typeof payload === 'string') {
      const value = payload.trim();
      if (!value || value.startsWith('<')) return null;

      try {
        return this.extractBackendDetail(JSON.parse(value));
      } catch {
        return value.length <= 300 ? value : null;
      }
    }

    if (!payload || typeof payload !== 'object') return null;

    const record = payload as Record<string, unknown>;
    const detail = record['detail'];
    if (Array.isArray(detail)) {
      const messages = detail
        .slice(0, 3)
        .map((item) => this.describeValidationItem(item))
        .filter((item): item is string => Boolean(item));
      return messages.length ? messages.join(' ') : null;
    }

    for (const candidate of [detail, record['message'], record['error'], record['reason']]) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
      if (candidate && typeof candidate === 'object') {
        const nestedMessage = (candidate as Record<string, unknown>)['message'];
        if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
          return nestedMessage.trim();
        }
      }
    }

    return null;
  }

  private describeValidationItem(item: unknown): string | null {
    if (!item || typeof item !== 'object') return null;

    const record = item as Record<string, unknown>;
    const location = Array.isArray(record['loc']) ? record['loc'] : [];
    const relevantLocation = location.filter((part) => part !== 'body');
    const lastPart = relevantLocation.at(-1);
    const numericIndex = typeof lastPart === 'number'
      ? lastPart
      : typeof lastPart === 'string' && /^\d+$/.test(lastPart)
        ? Number(lastPart)
        : null;
    const rawField = numericIndex === null ? lastPart : relevantLocation.at(-2);
    const field = rawField == null ? null : this.translateValidationField(String(rawField));
    const rawMessage = typeof record['msg'] === 'string' ? record['msg'] : '';
    const translatedMessage = this.translateValidationMessage(rawMessage, record);

    if (!translatedMessage) return null;
    const itemPosition = numericIndex === null ? '' : `, elemento ${numericIndex + 1}`;
    return field ? `Campo «${field}»${itemPosition}: ${translatedMessage}` : translatedMessage;
  }

  private translateValidationField(field: string): string {
    const labels: Record<string, string> = {
      email: 'correo',
      emails: 'correos',
      reply_to: 'correo de respuesta',
      email_reply_to: 'correo de respuesta',
      user_ids: 'identificadores de usuario',
      notification_type_id: 'tipo de notificación',
      scheduled_at: 'fecha programada',
      age_min: 'edad mínima',
      age_max: 'edad máxima',
      user_id: 'ID de usuario',
      related_transaction_id: 'transacción original',
      min_amount: 'importe mínimo',
      max_amount: 'importe máximo',
      title: 'título',
      body: 'contenido',
      subject: 'asunto',
      body_html: 'contenido del correo',
      language: 'idioma',
    };
    return labels[field] ?? field.replaceAll('_', ' ');
  }

  private translateValidationMessage(message: string, error: Record<string, unknown>): string {
    const normalized = message.trim().toLowerCase();
    const context = error['ctx'] && typeof error['ctx'] === 'object'
      ? error['ctx'] as Record<string, unknown>
      : {};
    if (!normalized) return '';
    if (normalized === 'field required') return 'es obligatorio.';
    if (normalized.includes('valid email')) return 'debe contener un correo válido.';
    if (normalized.includes('valid uuid')) return 'debe contener un identificador UUID válido.';
    if (normalized.includes('valid datetime') || normalized.includes('valid date')) {
      return 'debe contener una fecha y hora válidas.';
    }
    if (normalized.includes('timezone') || normalized.includes('time zone')) {
      return 'debe incluir una zona horaria explícita.';
    }
    if (normalized.includes('greater than or equal')) {
      return `debe ser mayor o igual a ${context['ge'] ?? 'el mínimo permitido'}.`;
    }
    if (normalized.includes('less than or equal')) {
      return `debe ser menor o igual a ${context['le'] ?? 'el máximo permitido'}.`;
    }
    if (normalized.includes('at least') && context['min_length'] != null) {
      return `debe tener al menos ${context['min_length']} caracteres.`;
    }
    if (normalized.includes('at most') && context['max_length'] != null) {
      return `debe tener como máximo ${context['max_length']} caracteres.`;
    }
    if (normalized.includes('input should be')) return 'contiene un valor no permitido.';
    if (normalized.includes('at least')) return 'no alcanza el mínimo requerido.';
    return message.trim();
  }
}
